// Parses an uploaded MusicXML snippet into a simple note list for the
// guided exercise player. Supports a single monophonic part: notes, rests,
// dotted values and ties. Chords collapse to their first note, grace notes
// and secondary voices are skipped.

const NATURAL_SEMITONE = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

export function parseExerciseXML(xmlString) {
  const parser = new DOMParser();
  // DOMParser chokes on some DOCTYPE declarations; the DTD adds nothing here
  const cleaned = xmlString.replace(/<!DOCTYPE[\s\S]*?>/i, '');
  const doc = parser.parseFromString(cleaned, 'application/xml');

  if (doc.querySelector('parsererror')) {
    throw new Error('File is not valid XML');
  }
  const root = doc.querySelector('score-partwise');
  if (!root) {
    throw new Error('File is not a MusicXML score-partwise document (export as uncompressed .musicxml from MuseScore)');
  }
  const part = root.querySelector('part');
  if (!part) {
    throw new Error('No part found in the MusicXML file');
  }

  const title =
    doc.querySelector('work work-title')?.textContent?.trim() ||
    doc.querySelector('movement-title')?.textContent?.trim() ||
    null;

  // Tempo marking, if the file carries one (MuseScore exports <sound tempo="...">)
  const soundEl = root.querySelector('sound[tempo]');
  const suggestedTempo = soundEl ? Math.round(parseFloat(soundEl.getAttribute('tempo'))) || null : null;

  const fifths = parseInt(part.querySelector('measure attributes key fifths')?.textContent ?? '0', 10) || 0;
  const beats = parseInt(part.querySelector('measure attributes time beats')?.textContent ?? '4', 10) || 4;
  const beatType = parseInt(part.querySelector('measure attributes time beat-type')?.textContent ?? '4', 10) || 4;

  let divisions = 1;
  let primaryVoice = null;
  let previousNote = null; // for tie merging
  const measures = [];
  let minMidi = Infinity;
  let maxMidi = -Infinity;

  for (const measureEl of part.querySelectorAll(':scope > measure')) {
    const divEl = measureEl.querySelector('attributes divisions');
    if (divEl) divisions = parseInt(divEl.textContent, 10) || divisions;

    const measureNotes = [];
    for (const noteEl of measureEl.querySelectorAll(':scope > note')) {
      if (noteEl.querySelector('grace')) continue;
      if (noteEl.querySelector('chord')) continue; // keep only the first note of a chord

      const voice = noteEl.querySelector('voice')?.textContent ?? '1';
      if (primaryVoice === null) primaryVoice = voice;
      if (voice !== primaryVoice) continue;

      const quarters = (parseInt(noteEl.querySelector('duration')?.textContent ?? '0', 10) || 0) / divisions;
      if (quarters <= 0) continue;

      if (noteEl.querySelector('rest')) {
        measureNotes.push({ rest: true, quarters });
        previousNote = null;
        continue;
      }

      const step = noteEl.querySelector('pitch step')?.textContent;
      const octave = parseInt(noteEl.querySelector('pitch octave')?.textContent ?? '', 10);
      if (!step || !(step in NATURAL_SEMITONE) || Number.isNaN(octave)) continue;
      const alter = parseInt(noteEl.querySelector('pitch alter')?.textContent ?? '0', 10) || 0;
      const midi = (octave + 1) * 12 + NATURAL_SEMITONE[step] + alter;

      const isTieStop = [...noteEl.querySelectorAll('tie')].some((t) => t.getAttribute('type') === 'stop');
      if (isTieStop && previousNote && !previousNote.rest && previousNote.midi === midi) {
        previousNote.quarters += quarters;
        continue;
      }

      const lyric = noteEl.querySelector('lyric text')?.textContent?.trim() || null;
      const note = { rest: false, midi, quarters, lyric };
      measureNotes.push(note);
      previousNote = note;
      minMidi = Math.min(minMidi, midi);
      maxMidi = Math.max(maxMidi, midi);
    }
    if (measureNotes.length > 0) measures.push(measureNotes);
  }

  const noteCount = measures.flat().filter((n) => !n.rest).length;
  if (noteCount === 0) {
    throw new Error('No notes found in the MusicXML file');
  }

  return {
    title,
    fifths,
    beats,
    beatType,
    quartersPerMeasure: beats * (4 / beatType),
    suggestedTempo,
    measures,
    noteCount,
    minMidi,
    maxMidi
  };
}

// Major tonic pitch class implied by a key signature (circle of fifths)
export function fifthsToMajorTonicPc(fifths) {
  return ((fifths * 7) % 12 + 12) % 12;
}
